import React, { useEffect, useState } from 'react';

import { FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';

import {
  adminActions,
  getPharmagapTransactionFilter,
  getPharmagapTransactions,
  isLoadingSelector,
  pharmagapTransactionList,
} from '@pharmaplan/common';

import useUserProfileLink from '../../../hooks/Admin/useUserProfileLink';
import { IDynamicTableObject } from '../../DynamicTable/types';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import DynamicTable from '../../DynamicTable';

import PharmagapTransactionHeaders from './PharmagapTransactionHeaders';
import { transactionHeaders, transactionRowConfig } from './helper';
import { renderScreen, setOpen } from '../../../actions/drawerActions';
import { MomentTimeFormats, ScreenTypes } from '../../../helpers/Constants';
import useTableSort from '../../../hooks/useTableSort';

const { getPharmagapTransactions: getTransactionsAction } = adminActions;

const loadingActions = [getTransactionsAction];

const PendingRequests = () => {
  const dispatch = useAppDispatch();
  const { isDesc, orderBy, handleSorting, order, resetSorting } = useTableSort();

  const [page, setPage] = useState(1);

  const list = useAppSelector(pharmagapTransactionList);
  const { data, totalCount } = list ?? {};

  const { goToUserProfile } = useUserProfileLink();

  const isLoading = useAppSelector((state) =>
    isLoadingSelector(loadingActions, state));

  useEffect(() => {
    dispatch(getPharmagapTransactions({ page: 1 }));
    dispatch(getPharmagapTransactionFilter());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePagination = (_: unknown, selectedPage: number) => {
    setPage(selectedPage + 1);
    dispatch(getPharmagapTransactions({ page }));
  };

  const goToDetails = (transactionId: string) =>
    () => {
      dispatch(setOpen(true));
      dispatch(
        renderScreen({
          screenType: ScreenTypes.transactions,
          screenNumber: 1,
          eventId: transactionId,
        }),
      );
    };

  const table: IDynamicTableObject = {
    headers: transactionHeaders(),
    rows: transactionRowConfig(data, goToDetails, goToUserProfile),
    headerBar: [],
  };

  const handleSubmit = (values: FormikValues) => {
    const {
      startDate,
      endDate,
      pharmacistId,
      transactionType,
      transactionStatus,
      pharmacyId,
    } = values ?? {};
    dispatch(
      getPharmagapTransactions({
        page,
        startDate: startDate
          ? (startDate as DateTime).toFormat(MomentTimeFormats.fullDate)
          : null,
        endDate: endDate
          ? (endDate as DateTime).toFormat(MomentTimeFormats.fullDate)
          : null,
        professionalId: pharmacistId,
        pharmacyId,
        sortBy: orderBy,
        desc: isDesc,
        transactionType,
        transactionStatus,
      }),
    );
  };

  const formik = useFormik({
    initialValues: {
      startDate: null,
      endDate: null,
      pharmacyId: '',
      pharmacies: '',
      pharmacySelected: '',
      pharmacistId: '',
      pharmacists: '',
      pharmacistSelected: '',
      status: '',
    },
    onSubmit: handleSubmit,
  });

  const reset = () => {
    setPage(1);
    formik.resetForm();
    resetSorting();
    dispatch(getPharmagapTransactions({ page: 1 }));
  };

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      handleSubmit(formik.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  return (
    <DynamicTable
      showHeader={false}
      customHeader={
        <PharmagapTransactionHeaders formik={formik} handleReset={reset} />
      }
      totalCount={totalCount}
      page={page}
      order={order}
      orderBy={orderBy}
      handleSort={handleSorting}
      handlePagination={handlePagination}
      loadSuccess={!isLoading}
      table={table}
    />
  );
};

export default PendingRequests;
